import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../client";

export const PLATFORM_ACCOUNTS_KEY = ["platform-accounts"] as const;

/** The cap the back office stores; mirrored so a request is never refused for its length. */
export const MAX_PLATFORM_ACCOUNT_LENGTH = 200;

export interface PlatformAccountDto {
  platformId: string;
  name: string;
  badgeColour: string;
  badgeLetters: string | null;
  accountPrompt: string | null;
  /** Null when the customer hasn't given one. */
  account: string | null;
  /** We are checking a membership against it, or have: it can't be changed or removed here. */
  locked: boolean;
}

interface ListPlatformAccountsResponse {
  items: PlatformAccountDto[];
}

/** Every platform we take memberships from, with the customer's account on it where they gave one. */
export function usePlatformAccounts() {
  return useQuery<PlatformAccountDto[]>({
    queryKey: PLATFORM_ACCOUNTS_KEY,
    queryFn: async () => {
      const res = await apiClient.get<ListPlatformAccountsResponse>("/customers/me/platform-accounts");
      return res.data.items;
    },
  });
}

export function useSetPlatformAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ platformId, account }: { platformId: string; account: string }) => {
      await apiClient.put(`/customers/me/platform-accounts/${platformId}`, { account });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PLATFORM_ACCOUNTS_KEY }),
  });
}

export function useRemovePlatformAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (platformId: string) => {
      await apiClient.delete(`/customers/me/platform-accounts/${platformId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PLATFORM_ACCOUNTS_KEY }),
  });
}
